/* ==========================================================================
   SHOESLI V5 — product page
   Variant selection, gallery, add-to-cart. Replaces Dawn's product-info.js.
   ========================================================================== */
(function () {
  'use strict';

  const money = (cents) => {
    if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);
    const f = (cents / 100).toFixed(2);
    return `₪${f}`;
  };

  /* ---------------------------------------------------------------------
     Gallery
     --------------------------------------------------------------------- */
  class V5Gallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.v5-gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('.v5-gallery__thumb'));
      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.show(thumb.dataset.mediaId))
      );
      document.addEventListener('v5:variant:change', (e) => {
        const id = e.detail && e.detail.featured_media && e.detail.featured_media.id;
        if (id) this.show(String(id));
      });
    }
    show(mediaId) {
      let found = false;
      this.slides.forEach((slide) => {
        const on = slide.dataset.mediaId === String(mediaId);
        if (on) found = true;
        slide.classList.toggle('is-active', on);
        slide.toggleAttribute('aria-hidden', !on);
      });
      if (!found) return;
      this.thumbs.forEach((thumb) => {
        const on = thumb.dataset.mediaId === String(mediaId);
        thumb.classList.toggle('is-active', on);
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }
  }
  if (!customElements.get('v5-gallery')) customElements.define('v5-gallery', V5Gallery);

  /* ---------------------------------------------------------------------
     Variant picker
     --------------------------------------------------------------------- */
  class V5Variants extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-v5-variant-data]');
      try {
        this.variants = JSON.parse(json.textContent);
      } catch (e) {
        this.variants = [];
      }
      // Resolve by id: closest('[data-section-id]') used to match this element
      // itself, so the hidden variant input was never found and the cart got
      // the first variant no matter which size was picked.
      this.root = document.getElementById(this.dataset.root) || document;
      this.addEventListener('change', (e) => {
        if (!e.target.matches('.v5-size__input')) return;
        this.select(e.target.value);
      });
    }

    select(id) {
      const variant = this.variants.find((v) => String(v.id) === String(id));
      if (!variant) return;

      // Keep the URL shareable and the back button meaningful.
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      const hidden = this.root.querySelector('[data-v5-variant-id]');
      if (hidden) hidden.value = variant.id;

      this.renderPrice(variant);
      this.renderButton(variant);

      document.dispatchEvent(new CustomEvent('v5:variant:change', { detail: variant }));
    }

    renderPrice(variant) {
      const host = this.root.querySelector('[data-v5-price]');
      if (!host) return;
      const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
      // Matches snippets/v5-price-variant.liquid: no percentage, no red.
      const label = (window.v5Strings && window.v5Strings.saleLabel) || '';
      host.innerHTML =
        `<span class="v5-price v5-price--lg${sale ? ' v5-price--sale' : ''}">` +
        `<span class="v5-price__now">${money(variant.price)}</span>` +
        (sale
          ? `<s class="v5-price__was">${money(variant.compare_at_price)}</s>` +
            (label ? `<span class="v5-price__tag">${label}</span>` : '')
          : '') +
        `</span>`;
    }

    renderButton(variant) {
      const btn = this.root.querySelector('[data-v5-atc]');
      const label = this.root.querySelector('[data-v5-atc-label]');
      if (!btn || !label) return;
      const strings = window.variantStrings || {};
      if (!variant.available) {
        btn.setAttribute('disabled', '');
        label.textContent = strings.soldOut || 'אזל';
      } else {
        btn.removeAttribute('disabled');
        label.textContent = strings.addToCart || 'הוספה לסל';
      }
    }
  }
  if (!customElements.get('v5-variants')) customElements.define('v5-variants', V5Variants);

  /* ---------------------------------------------------------------------
     Stock / delivery note. Copy comes from the DOM; this only toggles
     which of the merchant's two approved lines is shown.
     --------------------------------------------------------------------- */
  class V5StockNote extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-v5-stock-data]');
      try {
        this.states = JSON.parse(json.textContent);
      } catch (e) {
        this.states = {};
      }
      this.fast = this.querySelector('.v5-stock__line--fast');
      this.pre = this.querySelector('.v5-stock__line--pre');
      document.addEventListener('v5:variant:change', (e) => this.update(e.detail.id));
    }
    update(id) {
      const state = (this.states[String(id)] || '').trim();
      this.dataset.state = state || 'none';
      if (this.fast) this.fast.hidden = state !== 'fast';
      if (this.pre) this.pre.hidden = state !== 'pre';
    }
  }
  if (!customElements.get('v5-stock-note')) customElements.define('v5-stock-note', V5StockNote);

  /* ---------------------------------------------------------------------
     Add to cart
     --------------------------------------------------------------------- */
  class V5Add extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.button = this.querySelector('[data-v5-atc]');
      this.label = this.querySelector('[data-v5-atc-label]');
      this.spinner = this.querySelector('.v5-atc__spinner');
      this.error = this.querySelector('[data-v5-atc-error]');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      // Without a cart drawer the native POST is the more reliable path.
      if (!document.getElementById('V5CartDrawer')) return;
      e.preventDefault();
      if (this.button.hasAttribute('disabled')) return;

      this.setLoading(true);
      this.error.hidden = true;

      const body = new FormData(this.form);
      body.append('sections', 'v5-cart-drawer');
      body.append('sections_url', window.location.pathname);

      try {
        const res = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body,
        });
        const data = await res.json();

        if (data.status) {
          this.error.textContent = data.description || data.message;
          this.error.hidden = false;
          return;
        }
        document.dispatchEvent(
          new CustomEvent('v5:cart:added', { detail: { sections: data.sections } })
        );
      } catch (err) {
        this.error.textContent = (window.cartStrings && window.cartStrings.error) || '';
        this.error.hidden = false;
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(state) {
      this.button.classList.toggle('is-loading', state);
      this.button.setAttribute('aria-busy', String(state));
      if (this.label) this.label.hidden = state;
      if (this.spinner) this.spinner.hidden = !state;
    }
  }
  if (!customElements.get('v5-add')) customElements.define('v5-add', V5Add);
})();

/* ==========================================================================
   Sticky buy bar — mirrors the buy box once it scrolls out of view.
   ========================================================================== */
(function () {
  'use strict';

  class V5StickyBuy extends HTMLElement {
    connectedCallback() {
      if (window.matchMedia('(min-width: 990px)').matches) return;

      this.form = document.getElementById(this.dataset.form);
      this.realBtn = document.querySelector('[data-v5-atc]');
      this.btn = this.querySelector('[data-v5-sticky-add]');
      this.priceEl = this.querySelector('[data-v5-sticky-price]');
      this.sizeEl = this.querySelector('[data-v5-sticky-size]');
      if (!this.form || !this.realBtn || !this.btn) return;

      this.hidden = false;
      document.body.classList.add('has-stickybuy');

      // Delegate to the real control so validation, loading state and the
      // cart-drawer handoff all stay in one place.
      this.btn.addEventListener('click', () => this.realBtn.click());

      // Show the bar only while the real button is off-screen.
      new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting;
          this.classList.toggle('is-in', show);
          // Only lift the WhatsApp button while the bar actually occupies the
          // bottom edge, otherwise it floats for no reason.
          document.body.classList.toggle('stickybuy-in', show);
        },
        { rootMargin: '0px 0px -72px 0px' }
      ).observe(this.realBtn);

      document.addEventListener('v5:variant:change', (e) => this.sync(e.detail));
      // A drawer must never be competing with the bar for the bottom edge.
      document.addEventListener('v5:drawer:open', () => {
        this.classList.remove('is-in');
        document.body.classList.remove('stickybuy-in');
      });
    }

    sync(variant) {
      if (this.priceEl && window.Shopify && window.Shopify.formatMoney) {
        this.priceEl.textContent = window.Shopify.formatMoney(variant.price);
      } else if (this.priceEl) {
        this.priceEl.textContent = '₪' + (variant.price / 100).toFixed(2);
      }
      if (this.sizeEl) {
        const label = this.sizeEl.textContent.trim().split(/\s+/)[0] || '';
        this.sizeEl.textContent = `${label} ${String(variant.title).split(' / ')[0]}`.trim();
      }
      if (this.btn) {
        this.btn.disabled = !variant.available;
        this.btn.textContent = variant.available
          ? (window.variantStrings && window.variantStrings.addToCart) || this.btn.textContent
          : (window.variantStrings && window.variantStrings.soldOut) || this.btn.textContent;
      }
    }
  }
  if (!customElements.get('v5-sticky-buy')) customElements.define('v5-sticky-buy', V5StickyBuy);
})();
